/* eslint-disable max-len */
import React, { useEffect, useMemo, useState } from 'react'
import { Button, Grid, Tooltip, Typography } from '@material-ui/core'
import { ArrowLeftIcon, ShareIcon } from 'asterX-UI/Icons'
import { makeStyles } from '@material-ui/core/styles'
import { PageContainer } from 'components/PageContainer'
import { useParams } from 'react-router'
import { useLocation } from 'react-router'
import { useRoutes } from 'lib/hooks'
import { getValidDate } from 'lib/utils'
import useApplicationStore from 'context/ApplicationStore/useApplicationStore'
import { useAuthStore } from 'context/AuthStore'
import ActionModal from 'asterX-UI/Modals/ActionModal'
import { AsterChip } from 'asterX-UI/Chip'
import { GRAY_COLORS, SUCCESS_COLORS, ERROR_COLORS, WARNING_COLORS } from 'asterX-UI'
import FieldInfoNG from 'asterX-UI/DataDisplay/FieldInfoNG'
import { UserInfo } from 'asterX-UI/DataDisplay'
import { CONTRACT_PATH } from 'routes/constants.routes'
import { ErrorOutline } from '@material-ui/icons'
import { setByPath } from '../../lib/utils/mutiFormHelpers'
import AddDeferralModal from '../../components/AddDefferal/AddDeferralModal'
import { useStyles } from './application-page.styles'
import { ApplicationTabs } from './components/ApplicationTabs/ApplicationTabs'

const statusMeta = s => {
  switch (s) {
    case 'SUBMITTED':
      return { label: 'На согласовании', style: { background: WARNING_COLORS[100], color: WARNING_COLORS[800] } }
    case 'COMPLETED':
      return { label: 'Согласовано', style: { background: SUCCESS_COLORS[100], color: SUCCESS_COLORS[800] } }
    case 'REJECTED':
      return { label: 'Отклонено', style: { background: ERROR_COLORS[100], color: ERROR_COLORS[800] } }
    default:
      return { label: s || '-', style: { background: GRAY_COLORS[100], color: GRAY_COLORS[800] } }
  }
}

const useRejectionStyles = makeStyles(() => ({
  reasonBtn: {
    marginTop: 8,
    height: 32,
    lineHeight: '32px',
    padding: '0 10px',
    borderColor: '#e53935',
    color: '#e53935',
    fontWeight: 600,
    textTransform: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    transition: 'all .15s ease',
    '&:hover': {
      backgroundColor: '#ffe5e5',
      borderColor: '#e53935'
    }
  },
  tooltip: {
    backgroundColor: '#ffebee',
    color: '#b71c1c',
    border: '1px solid #ffcdd2',
    boxShadow: '0 6px 18px rgba(229,57,53,.15)',
    maxWidth: 520,
    padding: 12
  },
  arrow: { color: '#ffebee' }
}))

const typeLabel = t => {
  switch (t) {
    case 'SUSPENSION':
      return 'Приостановка платежей'
    default:
      return t || '-'
  }
}

const ApplicationPage = () => {
  const classes = useStyles()
  const rejectionClasses = useRejectionStyles()
  const { id } = useParams()
  const routes = useRoutes()
  const { goPrevPage } = routes
  const { search, pathname } = useLocation()
  const query = new URLSearchParams(search)
  const contractRedirectSourceuRL = query.get('return')
  const src = query.get('src')
  const { authStoreFunc } = useAuthStore()

  const {
    applicationStore: { application },
    applicationStoreFunc: {
      getApplicationById,
      approveApplication,
      rejectApplication,
      resubmitApplication,
      buildResubmitFormFromApplication
    }
  } = useApplicationStore()

  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')

  const [resubmitOpen, setResubmitOpen] = useState(false)
  const [resubmitForm, setResubmitForm] = useState(null)

  const canDecide = authStoreFunc.isAdmin()

  const statusChip = useMemo(() => {
    const s = statusMeta(application?.status)
    return (
      <AsterChip
        label={s.label}
        style={{
          ...s.style,
          border: `1px solid ${GRAY_COLORS[200]}`,
          fontWeight: 600,
          width: 'fit-content'
        }}
      />
    )
  }, [application])

  const onApprove = async () => {
    if (!application?.id) return
    await approveApplication(application.id)
  }

  const onRejectConfirm = async () => {
    if (!application?.id) return
    const result = await rejectApplication(rejectReason, application.id)
    if (result?.success) setRejectOpen(false)
  }

  const openResubmit = () => {
    if (!application) return
    setResubmitForm(buildResubmitFormFromApplication(application))
    setResubmitOpen(true)
  }

  const closeResubmit = () => {
    setResubmitOpen(false)
    setResubmitForm(null)
  }

  const onPathChange = e => {
    const { name, value } = e.target
    setResubmitForm(prev => setByPath(prev, name, value))
  }
  const onDateChange = (path, date) => setResubmitForm(prev => setByPath(prev, path, date))
  const onFileChange = (path, file) => setResubmitForm(prev => setByPath(prev, path, file))
  const onDeleteFile = path => setResubmitForm(prev => setByPath(prev, path, null))

  const onSubmitResubmit = async current => {
    const t = current?.deferralType || application?.applicationType
    if (!application?.id || !t) return

    if (t === 'SUSPENSION') {
      const s = current?.suspension || {}
      await resubmitApplication(application.id, {
        type: 'SUSPENSION',
        comment: 'Переотправка',
        form: {
          startDate: s.startDate,
          termMonths: s.termMonths,
          reason: s.reason,
          otherReason: s.otherReason,
          files: s.files || {}
        }
      })
    } else if (t === 'BUYBACK') {
      const b = current?.buyback || {}
      await resubmitApplication(application.id, {
        type: 'BUYBACK',
        comment: 'Переотправка',
        form: { car: { ...(b.car || {}) }, files: b.files || {} }
      })
    } else if (t === 'WRITE_OFF') {
      const w = current?.writeOff || {}
      await resubmitApplication(application.id, {
        type: 'WRITE_OFF',
        comment: 'Переотправка',
        form: { reason: w.reason, files: w.files || {} }
      })
    }
    closeResubmit()
  }

  const handleBack = () => {
    if (src === 'list' || src === 'applicationList') {
      routes.goTo('/applications')
      return
    }
    if (src === 'application' && contractRedirectSourceuRL) {
      return routes.push(decodeURIComponent(contractRedirectSourceuRL))
    }
    goPrevPage()
  }

  const redirectContractPage = () => {
    if (!application?.workListId) return
    const returnUrl = `${pathname}${search}`
    routes.push(`${CONTRACT_PATH}/${application.workListId}?src=application&return=${encodeURIComponent(returnUrl)}`)
  }

  const wl = application?.workListInfo || {}

  useEffect(() => {
    if (id) getApplicationById(id)
  }, [id])

  return (
    <PageContainer>
      <Grid container>
        <Grid item xs={12} className={classes.arrowLeft}>
          <Button size="small" onClick={handleBack} color="primary" className={classes.backButton}>
            <ArrowLeftIcon />
            Назад
          </Button>
        </Grid>
      </Grid>

      <Grid
        item
        container
        xs={12}
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={4}
        className={classes.header}
      >
        <Grid item xs={12} md={4}>
          <Typography className={classes.headerTitle}>{`Заявление № ${application?.id || '-'}`}</Typography>
          <Typography variant="body2">
            {`Дата создания:  ${getValidDate(
              application?.createdDate || '-'
            )}`}
          </Typography>
        </Grid>

        <Grid item xs={12} md={8} className={classes.headerItems}>
          <Grid item>
            <FieldInfoNG label="Тип заявления" isVertical>
              <AsterChip label={typeLabel(application?.applicationType)} />
            </FieldInfoNG>
          </Grid>

          <Grid item>
            {application?.status === 'REJECTED' ? (
              <FieldInfoNG label="Статус" isVertical style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <Tooltip
                  arrow
                  interactive
                  classes={{ tooltip: rejectionClasses.tooltip, arrow: rejectionClasses.arrow }}
                  enterDelay={150}
                  leaveDelay={80}
                  PopperProps={{
                    modifiers: {
                      offset: { enabled: true, offset: '0,8' },
                      preventOverflow: { enabled: true, boundariesElement: 'window' }
                    }
                  }}
                  title={
                    <div
                      style={{
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.45,
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        style={{
                          fontWeight: 700,
                          marginBottom: 10,
                          color: '#b71c1c',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <ErrorOutline /> Причина отказа
                      </Typography>
                      <Typography
                        style={{
                          fontWeight: 400,
                          marginBottom: 6,
                          color: GRAY_COLORS[800],
                          fontSize: 14,
                          border: '1px solid #b71c1c',
                          padding: '5px',
                          backgroundColor: 'white'
                        }}
                      >
                        {application.rejectionReason || 'Не заполнено'}
                      </Typography>
                    </div>
                  }
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'help',gap:4 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>{statusChip}</span>
                    <ErrorOutline style={{ color: '#e53935' }} />
                  </span>
                </Tooltip>
              </FieldInfoNG>
            ) : (
              <FieldInfoNG label="Статус" isVertical style={{ display: 'flex', flexDirection: 'column' }}>
                {statusChip}
              </FieldInfoNG>
            )}
          </Grid>

          <Grid item>
            <FieldInfoNG label="Номер договора" isVertical>
              <Typography variant="body2">
                <b>{wl?.contractNumber || '-'}</b>
              </Typography>
            </FieldInfoNG>
          </Grid>

          <Grid item>
            <FieldInfoNG label="Менеджер:" isVertical>
              <Grid item xs={12} container display="flex">
                <Typography variant="body2" className={classes.assignManager}>
                  {application?.createdByFullName || 'Нет данных менеджера'}
                </Typography>
              </Grid>
            </FieldInfoNG>
          </Grid>

          <Grid item>
            <UserInfo
              avatar={null}
              fullName={wl?.clientFullName || '-'}
              phone={wl?.clientPhone ? wl.clientPhone.toString()?.toPhone?.() : undefined}
              openTooltip={false}
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid item container xs={12} className={classes.actionButtons} style={{ gap: 12 }}>
        <Grid item style={{ display: 'flex', gap: 10 }}>
          {application?.status === 'REJECTED' && authStoreFunc.isManager() && (
            <Button
              variant="outlined"
              color="primary"
              style={{ minWidth: 220, height: 48, fontWeight: 600, transition: 'all .2s' }}
              onClick={openResubmit}
              disabled={!application?.id}
            >
              Переотправить
            </Button>
          )}
          {canDecide && application?.status === 'SUBMITTED' && (
            <>
              <Button
                variant="outlined"
                color="primary"
                style={{ minWidth: 220, height: 48, fontWeight: 600, transition: 'all .2s' }}
                onClick={onApprove}
                disabled={!application?.id}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = '#3f51b5'
                  e.currentTarget.style.color = '#fff'
                  e.currentTarget.style.borderColor = '#3f51b5'
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                  e.currentTarget.style.color = '#3f51b5'
                  e.currentTarget.style.borderColor = '#3f51b5'
                }}
              >
                Согласовать
              </Button>

              <Button
                variant="outlined"
                style={{
                  minWidth: 220,
                  height: 48,
                  fontWeight: 600,
                  borderColor: '#e53935',
                  color: '#e53935',
                  transition: 'all .2s'
                }}
                onClick={() => setRejectOpen(true)}
                disabled={!application?.id}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = '#e53935'
                  e.currentTarget.style.color = '#fff'
                  e.currentTarget.style.borderColor = '#e53935'
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                  e.currentTarget.style.color = '#e53935'
                  e.currentTarget.style.borderColor = '#e53935'
                }}
              >
                Отказать
              </Button>
            </>
          )}
        </Grid>
        {src !== 'application' && (
          <Grid item style={{ display: 'flex', gap: 10 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={redirectContractPage}
              disabled={!application?.workListId}
              style={{ height: 48, fontWeight: 600 }}
            >
              Перейти к контракту&nbsp;
              <ShareIcon />
            </Button>
          </Grid>
        )}
      </Grid>

      <Grid item xs={12}>
        <ApplicationTabs application={application} />
      </Grid>

      <ActionModal
        open={rejectOpen}
        onCancel={() => setRejectOpen(false)}
        onClose={() => setRejectOpen(false)}
        onConfirm={onRejectConfirm}
        title="Отказ по заявлению"
        labelConfirm="Отказать"
        disableConfirm={!rejectReason || !application?.id}
      >
        <Typography style={{ marginBottom: 12, fontWeight: 600 }} variant="body2">
          Причина отказа
        </Typography>
        <textarea
          rows={5}
          value={rejectReason}
          onChange={e => setRejectReason(e.target.value)}
          placeholder="Опишите причину"
          style={{ width: '95%', border: '1px solid #e0e0e0', borderRadius: 8, padding: 8 }}
        />
      </ActionModal>

      <AddDeferralModal
        open={resubmitOpen && !!resubmitForm}
        onClose={closeResubmit}
        onCancel={closeResubmit}
        mode="resubmit"
        value={resubmitForm}
        errors={{}}
        onPathChange={onPathChange}
        onDateChange={onDateChange}
        onFileChange={onFileChange}
        onDeleteFile={onDeleteFile}
        onSubmit={onSubmitResubmit}
      />
    </PageContainer>
  )
}

export default ApplicationPage
